import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useState } from 'react';
import { HttpError, disableLive, get, onSessionEnded, setCsrf, signIn, signOut } from './api/client';
import { config } from './api/config';
import type { List, Me, Session } from './api/types';
import type { PortalMe } from './api/portal';
import type { Client } from './api/clients';
import { PortalApp } from './screens/portal/portal';
import { JoinPage, isJoinPath } from './screens/portal/join';
import { Deleg8Bar } from './ui/bar';
import { ConfirmEmail } from './screens/confirm-email';
import { SignIn } from './screens/signin';
import { LoadError } from './ui/page';
import { ShellNotices } from './ui/shell';
import { navigate } from './router';

/** The team app (every team screen) is its own chunk: a client user's portal never downloads it. */
const TeamApp = lazy(() => import('./team-app'));

export interface AppState {
  me: Me;
  lists: List[];
  reloadLists: () => Promise<void>;
  /** The Clients this person owns or manages a board of (empty when the server has none). */
  clients: Client[];
  reloadClients: () => Promise<void>;
  sessions: Session[];
  people: { id: string; name: string; email: string | null }[];
  signOut: () => Promise<void>;
  /** Open the Add sheet; `text` (a pasted list) opens it on "Paste a list" with those lines. */
  openAdd: (listId?: string, text?: string) => void;
  openNewList: () => void;
}
export const AppCtx = createContext<AppState | null>(null);
export const useApp = () => useContext(AppCtx)!;

export function App() {
  return <><Screens /><ShellNotices /></>;
}

function Screens() {
  const [me, setMe] = useState<Me | null>(null);
  const [portal, setPortal] = useState<PortalMe | null>(null);
  const [state, setState] = useState<'loading' | 'in' | 'out' | 'offline' | 'broken'>('loading');
  const [broken, setBroken] = useState<unknown>(null);
  const [expired, setExpired] = useState(false);
  const load = useCallback(async () => {
    // /me and /portal/me alike: no answer at all is offline, a server that answers with an error is its own screen with Retry, and
    // only "not signed in" means Sign in.
    const failed = (e: unknown) => {
      if (e instanceof HttpError && e.status === 0) setState('offline');
      else if (e instanceof HttpError && e.status !== 401 && e.status !== 403) {
        setBroken(e);
        setState('broken');
      } else setState('out');
    };
    try {
      const m = await get<Me>('/me');
      setCsrf(m.csrfToken);
      setMe(m);
      setPortal(null);
      setExpired(false);
      setState('in');
    } catch (e) {
      if (e instanceof HttpError && e.status === 403 && (e.code as string) === 'portal_only') {
        // A customer's account: only /portal/* answers it (docs/PORTAL.md rule 2).
        try {
          const pm = await get<PortalMe>('/portal/me');
          setCsrf(pm.csrfToken);
          disableLive();
          setMe(null);
          setPortal(pm);
          setExpired(false);
          setState('in');
        } catch (pe) {
          failed(pe);
        }
      } else failed(e);
    }
  }, []);
  useEffect(() => void load(), [load]);
  // Could not reach the server: try again when the device says it is back online, and every few seconds (2, 4 ... 30 s).
  const offline = state === 'offline';
  useEffect(() => {
    if (!offline) return;
    let wait = 2000;
    let t: ReturnType<typeof setTimeout>;
    const again = () => { clearTimeout(t); void load(); };
    const later = () => { t = setTimeout(() => { again(); wait = Math.min(wait * 2, 30000); later(); }, wait); };
    const up = () => { wait = 2000; again(); later(); };
    addEventListener('online', up);
    later();
    return () => { clearTimeout(t); removeEventListener('online', up); };
  }, [offline, load]);
  useEffect(() => {
    onSessionEnded(() => { setExpired(true); setMe(null); setPortal(null); setState('out'); });
    return () => void onSessionEnded(null);
  }, []);
  if (location.pathname === '/confirm-email') return <ConfirmEmail />;
  if (state === 'loading') return <Loading />;
  if (state === 'broken') {
    return (
      <div className="flex h-full flex-col">
        {config.front === 'portal' ? null : <SignedOutBar />}
        <div className="min-h-0 flex-1 overflow-y-auto"><LoadError error={broken} onRetry={() => { setState('loading'); void load(); }} /></div>
      </div>
    );
  }
  const doSignIn = async () => { if (await signIn()) await load(); };
  // The invite link (<site>/portal/join#invite=<token>): the page works signed out, as a customer, or as someone else.
  if (isJoinPath()) {
    return <JoinPage signedIn={state === 'in'} onChanged={load} onSignOut={async () => { await signOut(); setMe(null); setPortal(null); setState('out'); }} />;
  }
  if (state === 'in' && portal) return <PortalApp me={portal} onOut={async () => { await signOut(); setPortal(null); setState('out'); navigate('/', true); }} />;
  if (state !== 'in' || !me) {
    return (
      <div className="flex h-full flex-col">
        {config.front === 'portal' ? null : <SignedOutBar onSignIn={doSignIn} />}
        <div className="min-h-0 flex-1"><SignIn offline={state === 'offline'} expired={expired} onSignIn={doSignIn} onDone={() => void load()} /></div>
      </div>
    );
  }
  // A signed-in session too old to link st8n comes back to /signin?auth_error=st8n_reauth: the sign-in screen, then `next`.
  if (location.pathname === '/signin' && new URLSearchParams(location.search).get('auth_error') === 'st8n_reauth') {
    return (
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1"><SignIn onSignIn={doSignIn} onDone={() => void load()} /></div>
      </div>
    );
  }
  return <Suspense fallback={<Loading />}><TeamApp me={me} onOut={async () => { await signOut(); setMe(null); setState('out'); }} /></Suspense>;
}

function Loading() {
  return <div className="flex h-full items-center justify-center"><span role="status" aria-label="Loading" className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-accent" /></div>;
}

/** Signed out: Deleg8's own header; "Sign in with st8n" only when this server has st8n sign-in set up (st8n can be on without
 *  it), and never on the error screen (no onSignIn: a button there would do nothing). */
function SignedOutBar({ onSignIn }: { onSignIn?: () => void }) {
  return <Deleg8Bar account={null} onSignIn={(config.mock ? config.st8nOn : config.st8nSignIn) ? onSignIn : undefined} />;
}


export const hue = (s: string) => [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 211);
/** A board's or a person's colour behind white initials: 4.5:1 or better on every hue (styles.css, `.tk-av`). */
export const swatch = (s: string) => `oklch(50% 0.13 ${hue(s)})`;
export const isDemo = () => config.mock;
